import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/topics?q=${encodeURIComponent(q)}` : "/topics");
    setMenuOpen(false);
  };

  const today = new Date();
  const dateLabel = `${today.getFullYear()} 年 ${today.getMonth() + 1} 月 ${today.getDate()} 日`;

  return (
    <>
      <header className="relative z-50">
        <div className="bg-foreground-950 text-background-100">
          <div className="mx-auto flex h-9 max-w-[1440px] items-center justify-between px-4 text-xs md:px-8">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-400 animate-soft-pulse" />
              <span className="truncate">{siteInfo.tagline} · 每日整理</span>
            </div>
            <div className="hidden items-center gap-5 sm:flex">
              <span className="text-background-300">{dateLabel}</span>
              <a
                href={`mailto:${siteInfo.email}`}
                className="transition-colors hover:text-primary-300"
              >
                {siteInfo.email}
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center gap-4 px-4 md:px-8">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-fire-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-black tracking-tight text-foreground-950">
                {siteInfo.name}
              </span>
              <span className="mt-0.5 text-[10px] tracking-[0.18em] text-foreground-500">
                GUAJI SHE
              </span>
            </span>
          </Link>

          <nav className="hidden flex-1 items-center gap-1 lg:flex" aria-label="主导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="ml-auto hidden items-center md:flex" role="search">
            <div className="flex h-10 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜索专题合集"
                aria-label="搜索专题合集"
                className="w-32 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-40"
              />
            </div>
          </form>

          <Link
            to="/topics"
            className="hidden whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800 lg:inline-block"
          >
            看全部合集
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-2-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="sticky top-16 z-30 border-b border-background-200 bg-background-50 lg:hidden">
          <nav className="mx-auto flex max-w-[1440px] flex-col gap-1 px-4 py-4" aria-label="移动端导航">
            <form onSubmit={submitSearch} className="mb-2 flex items-center gap-2" role="search">
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜索专题合集"
                className="h-10 flex-1 rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none focus:border-primary-400"
              />
              <button
                type="submit"
                className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50"
                aria-label="搜索"
              >
                <i className="ri-search-line text-base" />
              </button>
            </form>
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-primary-100 text-primary-700" : "text-foreground-700"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </>
  );
}